import React, { FunctionComponent, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

import secondsToTime from '@ps-int/ufo-helpers/lib/date/seconds-to-time';
import { dispatchOnDocumentChange } from '@ps-int/ufo-rocks/lib/helpers/document';
import { StreamPause } from '@yandex-360/iconostasis/dist/icons/StreamPause';
import { StreamPlay } from '@yandex-360/iconostasis/dist/icons/StreamPlay';
import { StreamStop } from '@yandex-360/iconostasis/dist/icons/StreamStop';
import { Popover2 } from '@yandex-360/orb-components/dist/components/Popover2/Popover2';
import { Text } from '@yandex-360/orb-components/dist/components/Text';
import cn from 'classnames';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';

import { BROADCAST_STATUSES_ABLE_TO_END } from '../../../../../../../../common/const/broadcast';
import { RootState } from '../../../../../../../init-store';
import { Button } from '../../../../../../shared/components';
import { ModalConfirmPure } from '../../../../../../shared/components/modal-confirm';
import MoreButtonPopover from '../../../../../../shared/components/more-button-popover';
import { MorePopupOption } from '../../../../../../shared/components/more-button-popover/option';
import Spin from '../../../../../../shared/components/spin';
import SvgImage from '../../../../../../shared/components/svg-image';
import { selectIsBroadcastEndEnabled } from '../../../../../../shared/features/config/selectors';
import { setBroadcastAccessLevel } from '../../../../../../shared/features/moderation/slice';
import { BroadcastTutorialStep, saveBroadcastTutorialShownAction } from '../../../../../../shared/features/settings';
import { i18n } from '../../../../../../shared/helpers/i18n';
import { countMetrika } from '../../../../../../shared/helpers/metrika';
import { AccessLevel, BroadcastStatus } from '../../../../../../types';
import {
    getBroadcastAccessLevel,
    getBroadcastStartedAt,
    getBroadcastStatus,
    getBroadcastTutorialStep,
    getBroadcastViewersCount,
    getConferenceId,
    isAccessRestrictionOrganizationAllowedSelector,
    isSelfModerator,
} from '../../../../selectors';
import {
    endBroadcastAction,
    shareBroadcastAction,
    startBroadcastAction,
    stopBroadcastAction,
} from '../../../broadcast/slice';
import { ToolbarContext } from '../../index';
import classes from './style.css';

const closeIcon = <SvgImage className={classes.BroadcastTooltip__CloseIcon} imageName="close" />;

const BROADCAST_TUTORIAL_STEPS: Partial<Record<BroadcastStatus, BroadcastTutorialStep>> = {
    [BroadcastStatus.CREATED]: BroadcastTutorialStep.STARTED,
    [BroadcastStatus.STARTED]: BroadcastTutorialStep.FINISHED,
    [BroadcastStatus.FINISHED]: BroadcastTutorialStep.COMPLETED,
};

const BROADCAST_STATUSES_WITHOUT_MENU = new Set<BroadcastStatus>([BroadcastStatus.STARTING, BroadcastStatus.ENDING]);

const matchBroadcastStatusWithTutorialStep = (status: BroadcastStatus, step?: BroadcastTutorialStep) =>
    !step ||
    (status === BroadcastStatus.CREATED && step === BroadcastTutorialStep.CREATED) ||
    (status === BroadcastStatus.STARTED && step === BroadcastTutorialStep.STARTED) ||
    (status === BroadcastStatus.FINISHED && step === BroadcastTutorialStep.FINISHED);

const shouldUpdateBroadcastTutorialStep = (
    nextStep: BroadcastTutorialStep,
    currentStep: BroadcastTutorialStep | unknown
) =>
    !currentStep ||
    (currentStep === BroadcastTutorialStep.STARTED && nextStep === BroadcastTutorialStep.FINISHED) ||
    (currentStep === BroadcastTutorialStep.FINISHED && nextStep === BroadcastTutorialStep.COMPLETED);

interface BroadcastButtonProps {
    isShowLimitationPopup: boolean;
    onShowBroadcastTutorialTooltip(): void;
    isCompact?: boolean;
}

const METRIKA_PATH = ['meeting_screen', 'stream_panel'];

const stateSelector = (state: RootState) => ({
    broadcastStatus: getBroadcastStatus(state),
    startedAt: getBroadcastStartedAt(state),
    isAccessRestrictionOrganizationAllowed: isAccessRestrictionOrganizationAllowedSelector(state),
    isModerator: isSelfModerator(state),
    currentAccessLevel: getBroadcastAccessLevel(state),
});

const BroadcastButton: FunctionComponent<BroadcastButtonProps> = ({
    isShowLimitationPopup,
    onShowBroadcastTutorialTooltip,
    isCompact,
}) => {
    const { broadcastStatus, startedAt, isAccessRestrictionOrganizationAllowed, isModerator, currentAccessLevel } =
        useSelector(stateSelector, shallowEqual);
    const viewersCount = useSelector(getBroadcastViewersCount);
    const isBroadcastEndEnabled = useSelector(selectIsBroadcastEndEnabled);

    const dispatch = useDispatch();

    const timerRef = useRef<ReturnType<typeof setInterval>>();
    const [broadcastDuration, setBroadcastDuration] = useState<number>(0);
    const [isEndModalOpened, setIsEndModalOpened] = useState(false);

    const openEndModal = useCallback(() => setIsEndModalOpened(true), []);
    const closeEndModal = useCallback(() => setIsEndModalOpened(false), []);

    const timerCallback = useCallback(() => {
        if (startedAt) {
            setBroadcastDuration(Math.round(Date.now() / 1000 - startedAt));
        }
    }, [startedAt]);

    useEffect(() => {
        if (broadcastStatus === BroadcastStatus.STARTED) {
            timerRef.current = setInterval(timerCallback, 1000);
        }

        return () => {
            if (timerRef.current) {
                clearInterval(timerRef.current);
            }
        };
    }, [broadcastStatus, timerCallback]);

    const previousBroadcasterStatusRef = useRef<BroadcastStatus | null>(null);
    useEffect(() => {
        previousBroadcasterStatusRef.current = broadcastStatus;
    }, [broadcastStatus]);

    const buttonRef = useRef<HTMLDivElement>(null);
    const [menuOpened, setMenuOpened] = useState(false);

    const onPopupClose = useCallback(() => {
        setMenuOpened(false);
    }, []);

    const conferenceId = useSelector(getConferenceId);
    const onCopyLinkClick = useCallback(() => {
        dispatch(shareBroadcastAction());
        countMetrika('meeting_screen', 'share_meeting', 'share_by_broadcast_link', conferenceId);
    }, [conferenceId, dispatch]);

    const onStartClick = useCallback(() => {
        dispatch(startBroadcastAction());
        countMetrika(...METRIKA_PATH, 'start');
    }, [dispatch]);

    const onPauseClick = useCallback(() => {
        dispatch(stopBroadcastAction());
        countMetrika(...METRIKA_PATH, 'stop');
    }, [dispatch]);

    const onEndOptionClick = useCallback(() => {
        openEndModal();
        countMetrika(...METRIKA_PATH, 'stream_ended');
    }, [openEndModal]);

    const onEndConfirm = useCallback(() => {
        closeEndModal();
        dispatch(endBroadcastAction({ notifyOnError: true }));
    }, [closeEndModal, dispatch]);

    const onAccessLevelClick = useCallback(() => {
        const level = currentAccessLevel === AccessLevel.ORGANIZATION ? AccessLevel.PUBLIC : AccessLevel.ORGANIZATION;
        dispatch(setBroadcastAccessLevel(level));
    }, [dispatch, currentAccessLevel]);

    const buttonContent = useMemo(() => {
        switch (broadcastStatus) {
            case BroadcastStatus.CREATED:
                return i18n('toolbar', 'broadcast-not-on-air');
            case BroadcastStatus.STARTING:
                return i18n('toolbar', 'broadcast-starting');
            case BroadcastStatus.STARTED:
                return (
                    <div
                        className={cn(classes.StartedButtonContent, {
                            [classes.StartedButtonContent_compact]: isCompact,
                        })}
                    >
                        {i18n('toolbar', 'broadcast-on-air') + ' ' + secondsToTime(broadcastDuration)}
                        <div className={classes.BroadcastButton__Viewers}>
                            {i18n('toolbar', 'broadcast-viewers-count', { count: viewersCount })}
                        </div>
                    </div>
                );
            case BroadcastStatus.ENDING:
                return i18n('toolbar', 'broadcast-ending');
            case BroadcastStatus.FINISHED:
                return i18n('toolbar', 'broadcast-paused');
        }
    }, [broadcastStatus, viewersCount, broadcastDuration, isCompact]);

    const tooltipText = useMemo(() => {
        switch (broadcastStatus) {
            case BroadcastStatus.CREATED:
                return i18n('toolbar', 'broadcast-not-on-air-tooltip');
            case BroadcastStatus.STARTING:
                return i18n('toolbar', 'broadcast-starting-tooltip');
            case BroadcastStatus.STARTED:
                return i18n('toolbar', 'broadcast-on-air-tooltip');
            case BroadcastStatus.FINISHED:
                return i18n('toolbar', 'broadcast-paused-tooltip');
        }
    }, [broadcastStatus]);

    const items = useMemo(() => {
        const items = [];

        if (isCompact) {
            items.push(
                <MorePopupOption
                    key="status"
                    className={classes.BroadcastMoreButtonPopover__Option}
                    disabled
                    title={tooltipText ?? broadcastStatus}
                    content={buttonContent}
                />
            );
            items.push(<div className={classes.BroadcastMoreButtonPopover__Divider}></div>);
        }

        items.push(
            <MorePopupOption
                key="copy-link"
                className={classes.BroadcastMoreButtonPopover__Option}
                onClick={onCopyLinkClick}
                iconName="link"
                title={i18n('toolbar', 'broadcast-copy-link')}
                content={i18n('toolbar', 'broadcast-copy-link')}
            />
        );

        if ([BroadcastStatus.CREATED, BroadcastStatus.FINISHED, BroadcastStatus.STARTING].includes(broadcastStatus)) {
            const title =
                broadcastStatus === BroadcastStatus.FINISHED ||
                (broadcastStatus === BroadcastStatus.STARTING &&
                    previousBroadcasterStatusRef.current === BroadcastStatus.FINISHED)
                    ? isBroadcastEndEnabled
                        ? i18n('toolbar', 'broadcast-continue-v2')
                        : i18n('toolbar', 'continue-broadcast')
                    : i18n('toolbar', 'start-broadcast');

            items.push(
                <MorePopupOption
                    key="copy-link"
                    className={classes.BroadcastMoreButtonPopover__Option}
                    onClick={onStartClick}
                    icon={<StreamPlay variant="outline" size="md" />}
                    disabled={broadcastStatus === BroadcastStatus.STARTING}
                    title={title}
                    content={title}
                />
            );
        } else if (broadcastStatus === BroadcastStatus.STARTED) {
            items.push(
                <MorePopupOption
                    key="stop"
                    className={classes.BroadcastMoreButtonPopover__Option}
                    onClick={onPauseClick}
                    icon={<StreamPause variant="outline" size="md" />}
                    title={i18n('toolbar', 'stop-broadcast')}
                    content={i18n('toolbar', 'stop-broadcast')}
                />
            );
        }

        if (isBroadcastEndEnabled && BROADCAST_STATUSES_ABLE_TO_END.has(broadcastStatus)) {
            items.push(
                <MorePopupOption
                    key="end"
                    className={classes.BroadcastMoreButtonPopover__Option}
                    onClick={onEndOptionClick}
                    icon={<StreamStop variant="outline" size="md" />}
                    title={i18n('toolbar', 'broadcast-end')}
                    content={i18n('toolbar', 'broadcast-end')}
                />
            );
        }

        if (isAccessRestrictionOrganizationAllowed && isModerator) {
            items.push(<div className={classes.BroadcastMoreButtonPopover__Divider}></div>);

            const title =
                currentAccessLevel === AccessLevel.ORGANIZATION
                    ? i18n('toolbar', 'access-level-public')
                    : i18n('toolbar', 'access-level-organization');
            const iconName = currentAccessLevel === AccessLevel.ORGANIZATION ? 'insecurity' : 'security';

            items.push(
                <MorePopupOption
                    key={iconName}
                    className={classes.BroadcastMoreButtonPopover__Option}
                    onClick={onAccessLevelClick}
                    iconName={iconName}
                    title={title}
                    content={title}
                />
            );
        }

        return items;
    }, [
        isCompact,
        onCopyLinkClick,
        broadcastStatus,
        isBroadcastEndEnabled,
        isAccessRestrictionOrganizationAllowed,
        isModerator,
        tooltipText,
        buttonContent,
        onStartClick,
        onPauseClick,
        onEndOptionClick,
        currentAccessLevel,
        onAccessLevelClick,
    ]);

    const [longStartTooltipClosed, setLongStartTooltipClosed] = useState(false);
    useEffect(() => {
        if (broadcastStatus !== BroadcastStatus.STARTING) {
            setLongStartTooltipClosed(false);
            dispatchOnDocumentChange();
        }
    }, [broadcastStatus]);

    const broadcastTutorialStep = useSelector(getBroadcastTutorialStep);
    const shouldShowTooltips =
        broadcastTutorialStep !== BroadcastTutorialStep.COMPLETED || broadcastStatus === BroadcastStatus.STARTING;

    const onCloseTooltip = useCallback(() => {
        if (broadcastStatus === BroadcastStatus.STARTING) {
            setLongStartTooltipClosed(true);
            return;
        }
        const nextStep = BROADCAST_TUTORIAL_STEPS[broadcastStatus];
        if (nextStep && shouldUpdateBroadcastTutorialStep(nextStep, broadcastTutorialStep)) {
            dispatch(saveBroadcastTutorialShownAction(nextStep));
        }
    }, [broadcastStatus, broadcastTutorialStep, dispatch]);

    const onClick = useCallback(() => {
        if (BROADCAST_STATUSES_WITHOUT_MENU.has(broadcastStatus)) {
            return;
        }

        onCloseTooltip();

        // https://st.yandex-team.ru/CHEMODAN-84895 - происходит из-за бага в LayerManager
        // Проверить актуальность после обновления yandex-lego/components@3.x.x
        setTimeout(() => {
            setMenuOpened(!menuOpened);
        }, 0);
    }, [broadcastStatus, menuOpened, onCloseTooltip]);

    const icon = useMemo(() => {
        switch (broadcastStatus) {
            case BroadcastStatus.STARTING:
            case BroadcastStatus.ENDING:
                return (
                    <div className={cn(classes.BroadcastButton__Spin)}>
                        <Spin size="md" color="white" progress />
                    </div>
                );
            case BroadcastStatus.FINISHED:
                return <SvgImage imageName="broadcastPaused" />;
            default:
                return <SvgImage imageName="broadcastEnabled" />;
        }
    }, [broadcastStatus]);

    const isBroadcastTooltipVisible =
        (shouldShowTooltips &&
            Boolean(tooltipText) &&
            matchBroadcastStatusWithTutorialStep(broadcastStatus, broadcastTutorialStep)) ||
        (broadcastStatus === BroadcastStatus.STARTING && !longStartTooltipClosed);

    useEffect(() => {
        if (isBroadcastTooltipVisible) {
            onShowBroadcastTutorialTooltip();
        }
    }, [isBroadcastTooltipVisible, onShowBroadcastTutorialTooltip]);

    const prevIsShowLimitationPopup = useRef(isShowLimitationPopup);
    useEffect(() => {
        if (isShowLimitationPopup && !prevIsShowLimitationPopup.current) {
            prevIsShowLimitationPopup.current = isShowLimitationPopup;
            if (isBroadcastTooltipVisible) {
                onCloseTooltip();
            }
        }
    }, [isShowLimitationPopup, onCloseTooltip, isBroadcastTooltipVisible]);

    const { buttonSize } = useContext(ToolbarContext);

    const iconProps = isCompact ? { icon } : { iconLeft: icon };

    return (
        <>
            <MoreButtonPopover
                placement={'top left'}
                className={classes.BroadcastMoreButtonPopover}
                triggerRef={buttonRef}
                isOpen={menuOpened}
                items={items}
                onClose={onPopupClose}
            />
            <div ref={buttonRef}>
                <Button
                    view="neutral"
                    size={buttonSize}
                    className={cn(classes.BroadcastButton, {
                        [classes.BroadcastButton_started]: broadcastStatus === BroadcastStatus.STARTED,
                        [classes.BroadcastButton_compact]: isCompact,
                    })}
                    onClick={onClick}
                    {...iconProps}
                >
                    {!isCompact ? buttonContent : undefined}
                </Button>
            </div>
            <ModalConfirmPure
                size="md"
                visible={isEndModalOpened}
                title={i18n('toolbar', 'broadcast-end-confirm-modal-header')}
                cancelLabel={i18n('toolbar', 'broadcast-end-confirm-modal-cancel')}
                confirmLabel={i18n('toolbar', 'broadcast-end-confirm-modal-confirm')}
                onClose={closeEndModal}
                onCancel={closeEndModal}
                onConfirm={onEndConfirm}
            >
                <Text className={classes.endModalContent} typography="body-md-regular">
                    {i18n('toolbar', 'broadcast-end-confirm-modal-content')}
                </Text>
            </ModalConfirmPure>
            {shouldShowTooltips ? (
                <Popover2
                    isOpen={isBroadcastTooltipVisible}
                    className={classes.BroadcastTooltip}
                    triggerRef={buttonRef}
                    placement="top"
                    preventInteractOutside={false}
                    autoFocus={false}
                >
                    <span>{tooltipText}</span>
                    <Button
                        className={classes.BroadcastTooltip__Close}
                        view="neutral-transparent"
                        size="xs"
                        onClick={onCloseTooltip}
                        icon={closeIcon}
                    />
                </Popover2>
            ) : null}
        </>
    );
};

export default BroadcastButton;
